import { useState } from "react";
import {
  useGetClientDocumentsQuery,
  useGetUserDocumentsQuery,
  useCreateUserDocumentMutation,
} from "../../../../../apis/userApi.hooks";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../../../utils/misc";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Label,
  DialogFooter,
} from "@pushpress/pushpress-ui";
import { useUserSubscriptionsQuery } from "../../../../../apis/subscriptionApi.hooks";
import { queryClient } from "../../../../../components/providers/ReactQueryProvider";
import type { CreateUserDocumentBody } from "../../../../../apis/userApi";

type AddDocumentsProps = {
  userUuid: string;
  clientUuid: string;
  showAddDocuments: boolean;
  setShowAddDocuments: (bool: boolean) => void;
  onConfirmation: () => void;
};

export default function AddDocumentsModal({
  clientUuid,
  userUuid,
  showAddDocuments,
  setShowAddDocuments,
  onConfirmation,
}: AddDocumentsProps) {
  const { data } = useGetClientDocumentsQuery(clientUuid);
  const { data: userDocumentData } = useGetUserDocumentsQuery(
    userUuid,
    clientUuid,
  );
  const { data: subscriptionData } = useUserSubscriptionsQuery(userUuid);
  const { mutate } = useCreateUserDocumentMutation();
  const [formData, setFormData] = useState<CreateUserDocumentBody>({
    documentUuid: "",
    subscriptionUuid: "",
  });
  const [showSubscriptionField, setShowSubscriptionField] =
    useState<boolean>(false);
  const documentData = data ?? [];
  const subscriptions = subscriptionData?.data ?? [];
  const userDocuments = userDocumentData ?? [];
  const userDocumentIds = userDocuments
    .filter((document) => document.active)
    .map((document) => document.document_id);

  // Filter out documents that a user already has
  const documents = documentData
    ? documentData.filter(
        (document) => !userDocumentIds.includes(document.parent_id),
      )
    : [];
  const closeModal = () => {
    setShowSubscriptionField(false);
    setShowAddDocuments(false);
  };
  const handleSave = () => {
    mutate(
      { userUuid, clientUuid, body: formData },
      {
        onSuccess: () => {
          toast.success("Added document to the user", defaultToastOptions);
          queryClient.invalidateQueries({
            queryKey: ["client-user-documents"],
          });
          setShowSubscriptionField(false);
          onConfirmation();
          closeModal();
        },
        onError: () => {
          toast.error(
            "There was an error adding the document",
            defaultToastOptions,
          );
        },
      },
    );
  };
  const onSelectDocument = (documentUuid: string) => {
    const document = documents.find(
      (document) => document.uuid == documentUuid,
    );
    const toSubmit = { ...formData };
    const { uuid, attach_to } = document;
    if (attach_to === "plan" || attach_to === "discount") {
      setShowSubscriptionField(true);
    } else {
      setShowSubscriptionField(false);
      toSubmit.subscriptionUuid = "";
    }
    setFormData({ ...toSubmit, documentUuid: uuid });
  };

  // disable confirmation if no document id or showing subscription field and no subscription uuid
  const disabled =
    !formData.documentUuid ||
    (showSubscriptionField && !formData.subscriptionUuid);
  return (
    <Dialog open={showAddDocuments} onOpenChange={closeModal}>
      <DialogContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Add Document to User</DialogTitle>
        </DialogHeader>
        {documents.length === 0 ? (
          <div> No documents available to add to this user</div>
        ) : (
          <Select name="documentUuid" onValueChange={onSelectDocument}>
            <Label className="tw-m-0">Document</Label>
            <SelectTrigger>
              <SelectValue placeholder="Select a document" />
            </SelectTrigger>
            <SelectContent className="tw-z-50">
              {documents.map((document) => (
                <SelectItem key={document.uuid} value={document.uuid}>
                  {document.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {showSubscriptionField && (
          <Select
            name="subscriptionUuid"
            onValueChange={(selectValue) =>
              setFormData({ ...formData, subscriptionUuid: selectValue })
            }
          >
            <Label className="tw-m-0">Plan</Label>
            <SelectTrigger>
              <SelectValue placeholder="Select a plan to associate this document with" />
            </SelectTrigger>
            <SelectContent className="tw-z-50">
              {subscriptions.map((subscription) => (
                <SelectItem key={subscription.uuid} value={subscription.uuid}>
                  {subscription.planObject.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            disabled={disabled}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
